import { useMemo, useState } from "react";
import { Badge, Col, Container, Row } from "react-bootstrap";
import CleaningEntry from "../../Components/Cleaning/CleaningEntry";
import { FieldGeneratorFieldType } from "../../Components/FormFields/FieldGenerator/FieldGenerator";
import QuickForm from "../../Components/Forms/QuickForm/QuickForm";
import { textBooleanHandler } from "../../Helpers/ValueHandlers/boolHandler";
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook";
import useBookingsByPropertyMemo from "../../Memos/Bookings/UseBookingsByPropertyMemo";
import timeService from "../../services/time/timeService";
import canAccess from "../../services/accessService/accessService";
import BookingModal, { useBookingModal } from "../../Components/Bookings/BookingModal/BookingModal";
import { useGetAdditionalServicesQuery, useGetBookingsQuery, useGetTurnoversQuery, useEditTurnoverMutation, useEditAdditionalServiceMutation, useFontaineSendCheckinReadyMutation, useFontaineSendCleanReadyMutation } from "../../services/bClientApi";
import { ConfirmModal } from "../../Components/Modals/confirmModal";
import { useDispatch } from "react-redux";
import { showSuccess, showError } from "../../redux/counter/counterSlice";
import { TurnoverType } from "../../Types/turnoverTypes";
import { AdditionalServiceType } from "../../Types/additionalServiceType";

const DATE_FORMAT = 'yyyy/LL/dd'

export default function CleaningPage() {

    const { myProperties, nonBlockingFontaineUnitPropertyIds } = useMyProperties({
        accessRules: { hideCleaning: true },
        filter: { hideParents: true }
    })

    const startOfToday = timeService.getCurrentTime().startOf('day').toMillis()
    const queryEndDate = timeService.getCurrentTime().plus({ weeks: 1 }).startOf('day').toMillis()
    const [
        activeQuery,
        setActiveQuery
    ] = useState<any>({
        query: {
            checkIn: startOfToday,
            checkOut: queryEndDate,
            bookingStatus: ['new', 'pending', 'confirmed'],
        },
        limit: 500,
        page: 1
    })


    const {
        data,
        isLoading,
        error,
    } = useGetBookingsQuery({
        bookingsQuery: {
            ...activeQuery.query,
            propertyId: Object.keys(myProperties || {}),
        },
        pagination: {
            page: activeQuery.page,
            limit: activeQuery.limit,
            all: true,
            sort: {}
        }
    }, {
        skip: Object.keys(myProperties).length === 0,
    })
    const bookingsList = data?.docs || []

    const {
        data: cleaningServicesData,
        isLoading: cleaningServicesLoading,
        error: cleaningServicesError,
    } = useGetAdditionalServicesQuery({
        additionalServicesQuery: {
            serviceDateStart: activeQuery.query.checkIn,
            serviceDateEnd: activeQuery.query.checkOut,
            serviceType: 'cleaning',
            status: ['new', 'pending', 'started', 'completed', 'ready'],
            // status: activeQuery.query.bookingStatus,
            propertyId: Object.keys(myProperties || {}),
        },
        pagination: {
            page: activeQuery.page,
            limit: activeQuery.limit,
            all: true,
            sort: {}
        }
    }, {
        skip: Object.keys(myProperties).length === 0
    })


    const {
        data: turnoversData,
        isLoading: turnoversLoading,
        error: turnoversError,
    } = useGetTurnoversQuery({
        turnoversQuery: {
            scheduledForStart: activeQuery.query.checkIn,
            scheduledForEnd: activeQuery.query.checkOut,
            propertyId: Object.keys(myProperties || {}),
        },
        pagination: {
            page: 1,
            limit: 500,
            all: true,
            sort: {}
        }
    }, {
        skip: Object.keys(myProperties).length === 0
    })

    const {
        data: overdueTurnoversData,
        isLoading: overdueTurnoversLoading,
        error: overdueTurnoversError,
    } = useGetTurnoversQuery({
        turnoversQuery: {
            scheduledForEnd: startOfToday - 1,
            status: ['new', 'pending', 'started', 'completed'],
            propertyId: Object.keys(myProperties || {}),
        },
        pagination: {
            page: 1,
            limit: 500,
            all: true,
            sort: {}
        }
    }, {
        skip: Object.keys(myProperties).length === 0
    })

    const {
        data: overdueServicesData,
        isLoading: overdueServicesLoading,
        error: overdueServicesError,
    } = useGetAdditionalServicesQuery({
        additionalServicesQuery: {
            serviceDateEnd: startOfToday - 1,
            serviceType: 'cleaning',
            status: ['new', 'pending', 'started', 'completed'],
            propertyId: Object.keys(myProperties || {}),
        },
        pagination: {
            page: 1,
            limit: 500,
            all: true,
            sort: {}
        }
    }, {
        skip: Object.keys(myProperties).length === 0
    })

    const turnoversByPropertyAndDay = useMemo(() => {
        const turnoversList: TurnoverType[] = turnoversData?.docs || []
        const ref: Record<string, TurnoverType> = {}
        turnoversList.forEach((t: TurnoverType) => {
            const day = timeService.getFormattedTime(t.scheduledFor, DATE_FORMAT)
            const key = `${t.propertyId}_${day}`
            ref[key] = t
        })
        return ref
    }, [turnoversData])

    const [editTurnover] = useEditTurnoverMutation()
    const [editAdditionalService] = useEditAdditionalServiceMutation()

    const onTurnoverStatusChange = async (turnover: TurnoverType, newStatus: string) => {
        try {
            await editTurnover({
                turnoverId: turnover._id!,
                turnoverInfo: { status: newStatus }
            }).unwrap()
            dispatch(showSuccess(`Turnover status updated to ${newStatus}`))
        } catch (err: any) {
            dispatch(showError(err?.data?.error || 'Failed to update turnover status'))
        }
    }

    const onAdditionalServiceStatusChange = async (service: AdditionalServiceType, newStatus: string) => {
        try {
            await editAdditionalService({
                additionalServiceId: service._id!,
                additionalServiceInfo: { status: newStatus }
            }).unwrap()
            dispatch(showSuccess(`Service status updated to ${newStatus}`))
        } catch (err: any) {
            dispatch(showError(err?.data?.error || 'Failed to update service status'))
        }
    }

    const {
        editBookingModal,
        clearBookingModal,
        onEditClick,
        onCheckOutClick,
        bookingModalonSubmit
    } = useBookingModal()

    const dispatch = useDispatch()
    const [sendCheckinReady] = useFontaineSendCheckinReadyMutation()
    const [sendCleanReady] = useFontaineSendCleanReadyMutation()
    const [readyModal, setReadyModal] = useState<{ active: boolean, propertyId: string }>({ active: false, propertyId: '' })
    const [cleanReadyModal, setCleanReadyModal] = useState<{ active: boolean, propertyId: string }>({ active: false, propertyId: '' })

    const onReadyClick = (propertyId: string) => {
        setReadyModal({ active: true, propertyId })
    }

    const onReadyConfirm = async () => {
        try {
            const response = await sendCheckinReady({ propertyIdKeys: [readyModal.propertyId] }).unwrap()
            dispatch(showSuccess(response.message || 'Checkin ready email sent'))
        } catch (err: any) {
            dispatch(showError(err?.data?.error || 'Failed to send checkin ready email'))
        }
        setReadyModal({ active: false, propertyId: '' })
    }

    const onCleanReadyClick = (propertyId: string) => {
        setCleanReadyModal({ active: true, propertyId })
    }

    const onCleanReadyConfirm = async () => {
        try {
            const response = await sendCleanReady({ propertyIdKeys: [cleanReadyModal.propertyId], bypass: true }).unwrap()
            dispatch(showSuccess(response.message || 'Clean ready email sent'))
        } catch (err: any) {
            dispatch(showError(err?.data?.error || 'Failed to send clean ready email'))
        }
        setCleanReadyModal({ active: false, propertyId: '' })
    }

    const [generalFilters, setGeneralFilters] = useState<any>({
        showCheckOuts: false,
        showCheckIns: false,
        showEmpty: false,
        showOccupied: false,
        showCleanings: true,
        showOverdue: true,
        showToday: true,
        allProperties: false,
    })

    // const [expandAllProperties, setExpandAllProperties] = useState<boolean>(false)
    // const [openProperties, setOpenProperties] = useState<any>({})

    const bookingsByProperty = useBookingsByPropertyMemo(bookingsList, {
        propertyData: myProperties ? myProperties : {},
        bookingProcessor: (bookingList: any[]) => {
            // return bookingList.sort((x: any, y: any) => timeService.dateToMillis(x.checkOut) - timeService.dateToMillis(y.checkOut)).map(
            return bookingList.sort((x: any, y: any) => timeService.dateToMillis(x.checkIn) - timeService.dateToMillis(y.checkIn)).map(
                (x: any, idx: number, a: any[]) => {
                    const b = { ...x, }
                    if (idx < a.length - 1) { b.nextCheckIn = a[idx + 1] }
                    return b
                }
            )
        }
    })

    const overdueTurnovers = useMemo(() => {
        const list: TurnoverType[] = overdueTurnoversData?.docs || []
        if (!myProperties || Object.keys(myProperties).length === 0) return []
        return list.filter((t: TurnoverType) => {
            if (!myProperties[t.propertyId]) return false
            if (!generalFilters.allProperties && !myProperties[t.propertyId]?.services?.cleaning) return false
            if (!myProperties[t.propertyId].public) return false
            if (
                generalFilters.propertyFilter &&
                JSON.stringify(myProperties[t.propertyId]).toLowerCase().indexOf(generalFilters.propertyFilter.toLowerCase()) === -1
            ) return false
            return true
        })
    }, [overdueTurnoversData, myProperties, generalFilters.allProperties, generalFilters.propertyFilter])

    const overdueServices = useMemo(() => {
        const list: AdditionalServiceType[] = overdueServicesData?.docs || []
        if (!myProperties || Object.keys(myProperties).length === 0) return []
        return list.filter((s: AdditionalServiceType) => {
            if (!myProperties[s.propertyId]) return false
            if (!generalFilters.allProperties && !myProperties[s.propertyId]?.services?.cleaning) return false
            if (!myProperties[s.propertyId].public) return false
            if (
                generalFilters.propertyFilter &&
                JSON.stringify(myProperties[s.propertyId]).toLowerCase().indexOf(generalFilters.propertyFilter.toLowerCase()) === -1
            ) return false
            return true
        })
    }, [overdueServicesData, myProperties, generalFilters.allProperties, generalFilters.propertyFilter])

    const cleaningsByProperty = useMemo(() => {
        const cleaningServicesList = cleaningServicesData?.docs || []
        const propertyHash: any = {}
        cleaningServicesList.forEach((service: any) => {
            if (!propertyHash[service.propertyId]) {
                propertyHash[service.propertyId] = []
            }
            propertyHash[service.propertyId].push(service)
        })
        return Object.keys(propertyHash).map(propertyId => {
            return {
                propertyId,
                cleanings: propertyHash[propertyId]
            }
        })
    }, [cleaningServicesData])

    const bookingsByDay = useMemo(() => {
        const skipProperty = (pId: string) => {
            if (myProperties) {
                if (
                    generalFilters.propertyFilter &&
                    myProperties[pId] &&
                    JSON.stringify(myProperties[pId]).toLowerCase().indexOf(generalFilters.propertyFilter.toLowerCase()) === -1
                ) {
                    return true
                }
                if (!myProperties[pId]) {
                    return true
                }
                if (!generalFilters.allProperties && !myProperties[pId]?.services?.cleaning) {
                    return true
                }
                if (!myProperties[pId].public) {
                    return true
                }
            }
            return false
        }
        // Calendar Bookings component

        const today = timeService.getCurrentTime()
        const todayDay = today.toFormat(DATE_FORMAT)
        const todayTime = today.toMillis()

        const bookingByDayList: any[] = []

        if (isLoading || cleaningServicesLoading || turnoversLoading || Object.keys(myProperties).length === 0 || overdueTurnoversLoading || overdueServicesLoading) {
            return bookingByDayList
        }

        let currentDate = timeService.createLuxonDate(activeQuery.query.checkIn).minus({ days: 1 })
        let currentDay = currentDate.toFormat(DATE_FORMAT)
        // let currentDay = timeService.createLuxonDate(activeQuery.query.checkIn).minus({ days: 1 }).toFormat(DATE_FORMAT)
        const endDate = timeService.getFormattedTime(activeQuery.query.checkOut, DATE_FORMAT)

        while (currentDay !== endDate) {
            currentDate = currentDate.plus({ days: 1 })
            currentDay = currentDate.toFormat(DATE_FORMAT)
            // currentDay = timeService.createLuxonDate(currentDay).plus({ days: 1}).toFormat(DATE_FORMAT)

            const day = currentDate.toMillis()
            // const day = timeService.createLuxonDate(currentDay).toMillis()

            const bookedProperties: { [v: string]: boolean } = {}

            const propertyWithBookingsRef: any = {}

            const currentDayInfo: any = {
                day: currentDay,
                isToday: currentDay === todayDay,
                bookings: [],
                emptyProperties: [],
                emptyRightNow: [],
                emptyRightNowRef: {},
                activeReservations: [],
                checkIns: [],
                checkOuts: [],
                cleanings: [],
            }

            const consumedTurnoverKeys = new Set<string>()

            for (const p of bookingsByProperty) {
                propertyWithBookingsRef[p.propertyId] = p

                const shouldSkipProperty = skipProperty(p.propertyId)
                if (shouldSkipProperty) { continue }

                for (const b of p.bookings) {

                    const isRepairOrBlocked = b.source === 'repairs' || b.source === 'blocked'

                    const checkIn = timeService.dateToMillis(b.checkIn)
                    const checkInDay = timeService.getFormattedTime(b.checkIn, DATE_FORMAT)
                    const checkOut = timeService.dateToMillis(b.checkOut)
                    const checkOutDay = timeService.getFormattedTime(b.checkOut, DATE_FORMAT)
                    //mark booked properties and active reservations
                    if (
                        (checkIn <= day && day <= checkOut) ||
                        checkInDay === currentDay ||
                        checkOutDay === currentDay
                    ) {
                        bookedProperties[p.propertyId] = true
                        currentDayInfo.bookings.push(b)
                        if (checkOutDay !== currentDay && !isRepairOrBlocked) {
                            currentDayInfo.activeReservations.push(b)
                        }
                    }
                    // handle checkins
                    if (checkInDay === currentDay && !isRepairOrBlocked && !isRepairOrBlocked) {
                        currentDayInfo.checkIns.push(b)
                    }
                    // handle checkouts and create cleaning entries
                    if (checkOutDay === currentDay) {
                        if (!isRepairOrBlocked) {
                            currentDayInfo.checkOuts.push(b)
                        }
                        const checkOutDay2 = timeService.getFormattedTime(b.checkOut, DATE_FORMAT)
                        const turnoverKey = `${b.propertyId}_${checkOutDay2}`
                        if (turnoversByPropertyAndDay[turnoverKey]) {
                            consumedTurnoverKeys.add(turnoverKey)
                        }
                        const cleaningEntry = {
                            bookingId: b._id,
                            propertyId: b.propertyId,
                            serviceDate: b.checkOut,
                            serviceSubType: isRepairOrBlocked ? 'refresh' : 'checkout',
                            bookingInfo: b,
                            isBooking: true,
                            nextCheckIn: b.nextCheckIn,
                            turnoverInfo: turnoversByPropertyAndDay[turnoverKey] || null,
                        }
                        currentDayInfo.cleanings.push(cleaningEntry)
                    }

                }
            }

            for (const p of cleaningsByProperty) {
                const pId = p.propertyId
                const shouldSkipProperty = skipProperty(pId)
                if (shouldSkipProperty) { continue }

                for (const c of p.cleanings) {
                    const cleaningEntry = {
                        ...c,
                        additionalServiceInfo: c,
                    }
                    const serviceDateDay = timeService.getFormattedTime(c.serviceDate, DATE_FORMAT)
                    if (serviceDateDay === currentDay) {
                        const serviceBookingId = c.bookingId

                        const matchingBookingEntry = currentDayInfo.bookings.find((b: any) => b._id === serviceBookingId)
                        if (matchingBookingEntry) {
                            cleaningEntry.bookingInfo = matchingBookingEntry
                        }

                        const turnoverKey = `${pId}_${serviceDateDay}`
                        cleaningEntry.turnoverInfo = turnoversByPropertyAndDay[turnoverKey] || null
                        if (turnoversByPropertyAndDay[turnoverKey]) {
                            consumedTurnoverKeys.add(turnoverKey)
                        }

                        currentDayInfo.cleanings.push(cleaningEntry)
                    }
                }

            }

            // Add standalone turnovers (not associated with a booking checkout or additional service)
            for (const turnoverKey in turnoversByPropertyAndDay) {
                if (consumedTurnoverKeys.has(turnoverKey)) { continue }
                const turnover = turnoversByPropertyAndDay[turnoverKey]
                const turnoverDay = timeService.getFormattedTime(turnover.scheduledFor, DATE_FORMAT)
                if (turnoverDay !== currentDay) { continue }
                const shouldSkipProperty = skipProperty(turnover.propertyId)
                if (shouldSkipProperty) { continue }
                const cleaningEntry = {
                    propertyId: turnover.propertyId,
                    serviceDate: turnover.scheduledFor,
                    serviceSubType: turnover.type || 'turnover',
                    turnoverInfo: turnover,
                }
                currentDayInfo.cleanings.push(cleaningEntry)
            }

            if (myProperties) {
                for (const pId in myProperties) {
                    const shouldSkipProperty = skipProperty(pId)
                    if (shouldSkipProperty) { continue }

                    // Handle empty properties
                    if (!bookedProperties[pId]) {
                        const emptyPropertyEntry: any = {
                            id: pId,
                            nextCheckIn: null
                        }
                        if (propertyWithBookingsRef[pId]) {
                            const pNextCheckIn = propertyWithBookingsRef[pId].bookings.filter((b: any) => day < timeService.dateToMillis(b.checkIn))
                            if (pNextCheckIn.length > 0) {
                                emptyPropertyEntry.nextCheckIn = pNextCheckIn[0]
                            }
                        }
                        currentDayInfo.emptyProperties.push(emptyPropertyEntry)
                    }

                    // handle empty right now
                    if (currentDay === todayDay) {
                        const emptyRightNowEntry: any = {
                            id: pId,
                            propertyId: pId,
                            nextCheckIn: null,
                            nextCheckInToday: false,
                            checkOutToday: false,
                            occupied: false,
                        }

                        if (propertyWithBookingsRef[pId]) {
                            for (const b of propertyWithBookingsRef[pId].bookings) {

                                // if booking source is repair or blocked skip
                                if (b.source === 'repairs' || b.source === 'blocked') {
                                    continue
                                }

                                const checkIn = timeService.dateToMillis(b.checkIn)
                                const checkInDay = timeService.getFormattedTime(b.checkIn, DATE_FORMAT)
                                const checkOut = timeService.dateToMillis(b.checkOut)
                                const checkOutDay = timeService.getFormattedTime(b.checkOut, DATE_FORMAT)
                                // checks for active reservation for today
                                if (checkIn <= day && day <= checkOut) {
                                    // sees if there is a checkout today and mark is as potentially empty today
                                    if (checkOutDay === currentDay) {
                                        emptyRightNowEntry.checkOutToday = true
                                    }
                                }
                                // check right now right now
                                if (checkIn <= todayTime && todayTime <= checkOut) {
                                    emptyRightNowEntry.occupied = true
                                }
                                if (day < checkIn) {
                                    emptyRightNowEntry.nextCheckIn = b
                                    if (checkInDay === currentDay) {
                                        emptyRightNowEntry.nextCheckInToday = true
                                    }
                                    break
                                }
                            }
                        }

                        if (!emptyRightNowEntry.occupied) {
                            currentDayInfo.emptyRightNow.push(emptyRightNowEntry)
                            currentDayInfo.emptyRightNowRef[pId] = true
                        }
                    }
                }
            }

            bookingByDayList.push(currentDayInfo)
        }
        return bookingByDayList
    }, [myProperties, activeQuery.query.checkIn, activeQuery.query.checkOut, bookingsByProperty, cleaningsByProperty, turnoversByPropertyAndDay, isLoading, cleaningServicesLoading, turnoversLoading, overdueTurnoversLoading, generalFilters.allProperties, generalFilters.propertyFilter, overdueServicesLoading])

    console.log('rendering')

    return (
        <Container>
            <Row>
                <Col>
                    <h1>Cleaning</h1>
                    <hr />
                    {error && (<>{JSON.stringify(error)}<br /></>)}
                    {cleaningServicesError && (<>{JSON.stringify(cleaningServicesError)}<br /></>)}
                    {turnoversError && (<>{JSON.stringify(turnoversError)}<br /></>)}
                    {overdueTurnoversError && (<>{JSON.stringify(overdueTurnoversError)}<br /></>)}
                    {overdueServicesError && (<>{JSON.stringify(overdueServicesError)}<br /></>)}

                    <QuickForm
                        resetText="Clear Filters"
                        onReset={() => {
                            setGeneralFilters({
                                showCheckOuts: false,
                                showCheckIns: false,
                                showEmpty: false,
                                showOccupied: false,
                                showCleanings: false,
                                showOverdue: false,
                                showToday: false,
                                allProperties: false,
                                propertyFilter: '',
                            })
                            return {
                                dateRange: null,
                                checkIn: new Date(activeQuery.query.checkIn),
                                checkOut: new Date(activeQuery.query.checkOut),
                                showCheckOuts: false,
                                showCheckIns: false,
                                showEmpty: false,
                                showOccupied: false,
                                showCleanings: false,
                                showOverdue: false,
                                showToday: false,
                                allProperties: false,
                                propertyFilter: '',
                            }
                        }}
                        initialData={{
                            dateRange: null,
                            checkIn: new Date(activeQuery.query.checkIn),
                            checkOut: new Date(activeQuery.query.checkOut),
                            showCheckOuts: false,
                            showCheckIns: false,
                            showEmpty: false,
                            showOccupied: false,
                            showCleanings: true,
                            showOverdue: true,
                            showToday: true,
                            allProperties: false,
                            propertyFilter: '',
                        }}
                        fields={cleaningSearchFields}
                        onChange={(data: any) => {
                            const fieldName = data.target.name
                            const fieldValue = data.target.value
                            if (quickFilterFields[fieldName]) {
                                const newFilters = {
                                    ...generalFilters,
                                    [fieldName]: quickFilterFields[fieldName].handler ? quickFilterFields[fieldName].handler(fieldValue) : fieldValue,
                                }
                                setGeneralFilters(newFilters)
                            }
                        }}
                        onSubmit={(d: any) => {
                            if (d.checkIn && d.checkOut) {
                                const newQuery = {
                                    ...activeQuery,
                                    query: {
                                        ...activeQuery.query,
                                        checkIn: timeService.createLuxonDate(d.checkIn).startOf('day').toMillis(),
                                        checkOut: timeService.createLuxonDate(d.checkOut).endOf('day').toMillis(),
                                    }
                                }
                                setActiveQuery(newQuery)
                            }
                        }}
                    />


                    {/* {bookingsList?.length} Bookings <br /> */}

                    {generalFilters.showOverdue && overdueTurnovers.length > 0 && (
                        <div className="cleaning-wrapper">
                            <h4>
                                Overdue Turnovers <Badge bg="danger">{overdueTurnovers.length}</Badge>
                            </h4>
                            <hr />
                            {overdueTurnovers.map((t: TurnoverType, idx: number) => {
                                const propertyInfo = myProperties[t.propertyId]
                                return (
                                    <CleaningEntry
                                        key={`${t._id}-overdue-${idx}`}
                                        propertyInfo={propertyInfo}
                                        turnoverInfo={t}
                                        onTurnoverStatusChange={onTurnoverStatusChange}
                                        dayRef={timeService.getFormattedTime(t.scheduledFor, DATE_FORMAT)}
                                    />
                                )
                            })}
                        </div>
                    )}

                    {generalFilters.showOverdue && overdueServices.length > 0 && (
                        <div className="cleaning-wrapper">
                            <h4>
                                Overdue Services <Badge bg="danger">{overdueServices.length}</Badge>
                            </h4>
                            <hr />
                            {overdueServices.map((s: AdditionalServiceType, idx: number) => {
                                const propertyInfo = myProperties[s.propertyId]
                                return (
                                    <CleaningEntry
                                        key={`${s._id}-overdue-service-${idx}`}
                                        propertyInfo={propertyInfo}
                                        additionalServiceInfo={s}
                                        onAdditionalServiceStatusChange={onAdditionalServiceStatusChange}
                                        additionalCleaning={s.serviceSubType}
                                        dayRef={timeService.getFormattedTime(s.serviceDate, DATE_FORMAT)}
                                    />
                                )
                            })}
                        </div>
                    )}

                    {bookingsByDay.filter(day => !generalFilters.showToday || day.isToday).map((day) => (
                        <div key={day.day}>
                            <h3 style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'white',
                                zIndex: 10,
                                paddingTop: '10px',
                                paddingBottom: '10px',
                            }}> <b>{day.isToday ? 'TODAY' : ''}</b> {timeService.getOffsetFormattedTime(day.day, 'LL/dd/yyyy cccc')}</h3>
                            <hr />

                            {generalFilters.showCheckOuts && day.checkOuts && day.checkOuts.length > 0 && (
                                <div className="cleaning-wrapper">
                                    <h4>
                                        Checkouts <Badge bg="info">{day.checkOuts.length}</Badge>
                                    </h4>
                                    <hr />
                                    {day.checkOuts.map((b: any, idx: number) => {

                                        const propertyInfo = myProperties[b.propertyId]
                                        const canEditBooking = canAccess('routes', 'edit-booking', { payload: b })
                                        return (
                                            <CleaningEntry
                                                key={`${b._id}-checkout-${idx}`}
                                                bookingInfo={b}
                                                propertyInfo={propertyInfo}
                                                nextCheckIn={b.nextCheckIn}
                                                colorBg={day.isToday}
                                                onEdit={canEditBooking ? onEditClick : undefined}
                                                onCheckedOut={canEditBooking ? onCheckOutClick : undefined}
                                                onReady={nonBlockingFontaineUnitPropertyIds[b.propertyId] ? () => onCleanReadyClick(b.propertyId) : undefined}
                                                emptyRef={day.emptyRightNowRef[b.propertyId]}
                                            />
                                        )
                                    })}
                                </div>
                            )}

                            {generalFilters.showCleanings && day.cleanings && day.cleanings.length > 0 && (
                                <div className="cleaning-wrapper">
                                    <h4>
                                        Cleanings <Badge bg="info">{day.cleanings.length}</Badge>
                                    </h4>
                                    <hr />
                                    {day.cleanings.map((s: any, idx: number) => {

                                        const propertyInfo = myProperties[s.propertyId]

                                        return (
                                            <CleaningEntry
                                                key={`${s._id}-cleaning-${idx}`}
                                                bookingInfo={s.bookingInfo}
                                                propertyInfo={propertyInfo}
                                                nextCheckIn={s.nextCheckIn}
                                                dayRef={timeService.getFormattedTime(s.serviceDate, DATE_FORMAT)}
                                                colorBg={day.isToday}
                                                additionalCleaning={s.serviceSubType}
                                                emptyRef={day.emptyRightNowRef[s.propertyId]}
                                                turnoverInfo={s.turnoverInfo}
                                                onTurnoverStatusChange={onTurnoverStatusChange}
                                                additionalServiceInfo={s.additionalServiceInfo}
                                                onAdditionalServiceStatusChange={onAdditionalServiceStatusChange}
                                            />
                                        )
                                    })}
                                </div>
                            )}

                            {generalFilters.showCheckIns && day.checkIns && day.checkIns.length > 0 && (
                                <div className="cleaning-wrapper">
                                    <h4>
                                        Checkins <Badge bg="info">{day.checkIns.length}</Badge>
                                    </h4>
                                    <hr />
                                    {day.checkIns.map((b: any, idx: number) => {

                                        const propertyInfo = myProperties[b.propertyId]
                                        const canEditBooking = canAccess('routes', 'edit-booking', { payload: b })
                                        return (
                                            <CleaningEntry
                                                key={`${b._id}-checkin-${idx}`}
                                                bookingInfo={b}
                                                propertyInfo={propertyInfo}
                                                nextCheckIn={b.nextCheckIn}
                                                colorBg={day.isToday}
                                                onEdit={canEditBooking ? onEditClick : undefined}
                                                onReady={nonBlockingFontaineUnitPropertyIds[b.propertyId] ? () => onReadyClick(b.propertyId) : undefined}
                                                emptyRef={day.emptyRightNowRef[b.propertyId]}
                                            />
                                        )
                                    })}
                                </div>
                            )}

                            {generalFilters.showEmpty && day.isToday && day.emptyRightNow && day.emptyRightNow.length > 0 && (
                                <div className="cleaning-wrapper">
                                    <h4>
                                        Empty Right Now <Badge bg="info">{day.emptyRightNow.length}</Badge>
                                    </h4>
                                    <hr />
                                    {day.emptyRightNow.map((b: any, idx: number) => {

                                        const propertyInfo = myProperties[b.propertyId]

                                        return (
                                            <CleaningEntry
                                                key={`${b.id}-emptyRightNow-${idx}`}
                                                propertyInfo={propertyInfo}
                                                nextCheckIn={b.nextCheckIn}
                                                nextCheckInToday={b.nextCheckInToday}
                                                checkedOutToday={b.checkOutToday}
                                                dayRef={day.day}
                                            />
                                        )
                                    })}
                                </div>
                            )}

                            {generalFilters.showEmpty && !day.isToday && day.emptyProperties && day.emptyProperties.length > 0 && (
                                <div className="cleaning-wrapper">
                                    <h4>
                                        Empty Properties <Badge bg="info">{day.emptyProperties.length}</Badge>
                                    </h4>
                                    <hr />
                                    {day.emptyProperties.map((b: any, idx: number) => {

                                        const propertyInfo = myProperties[b.id]

                                        return (
                                            <CleaningEntry
                                                key={`${b.id}-emptyProperties-${idx}`}
                                                propertyInfo={propertyInfo}
                                                nextCheckIn={b.nextCheckIn}
                                                dayRef={day.day}
                                            />
                                        )
                                    })}
                                </div>
                            )}

                            {generalFilters.showOccupied && day.activeReservations && day.activeReservations.length > 0 && (
                                <div className="cleaning-wrapper">
                                    <h4>
                                        Occupied <Badge bg="info">{day.activeReservations.length}</Badge>
                                    </h4>
                                    <hr />
                                    {day.activeReservations.map((b: any, idx: number) => {

                                        const propertyInfo = myProperties[b.propertyId]
                                        const canEditBooking = canAccess('routes', 'edit-booking', { payload: b })
                                        return (
                                            <CleaningEntry
                                                key={`${b._id}-activeReservations-${idx}`}
                                                bookingInfo={b}
                                                propertyInfo={propertyInfo}
                                                nextCheckIn={b.nextCheckIn}
                                                colorBg={day.isToday}
                                                onEdit={canEditBooking ? onEditClick : undefined}
                                                emptyRef={day.emptyRightNowRef[b.propertyId]}
                                            />
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    ))}

                </Col>
            </Row>

            <BookingModal
                active={editBookingModal.active}
                booking={editBookingModal.booking}
                clearBookingModal={clearBookingModal}
                bookingModalonSubmit={bookingModalonSubmit}
            />

            <ConfirmModal
                title="Send Checkin Ready Email"
                message="Are you sure you want to send the checkin ready email for this property?"
                active={readyModal.active}
                onClose={() => setReadyModal({ active: false, propertyId: '' })}
                onConfirm={onReadyConfirm}
            />

            <ConfirmModal
                title="Send Clean Ready Email"
                message="Are you sure you want to send the clean ready email for this property? This will bypass the normal check and force the email."
                active={cleanReadyModal.active}
                onClose={() => setCleanReadyModal({ active: false, propertyId: '' })}
                onConfirm={onCleanReadyConfirm}
            />


        </Container>
    )
}
const boolHandler = {
    handler: textBooleanHandler
}

const quickFilterFields: any = {
    showCheckIns: boolHandler,
    showCheckOuts: boolHandler,
    showOccupied: boolHandler,
    showCleanings: boolHandler,
    showOverdue: boolHandler,
    showToday: boolHandler,
    showEmpty: boolHandler,
    allProperties: boolHandler,
    propertyFilter: {
        handler: (val: any) => val
    }
}

const cleaningSearchFields: FieldGeneratorFieldType[] = [
    {
        fieldName: 'dateRange',
        fieldLabel: 'Date Range',
        fieldType: 'dateRange',
        placeholder: 'mm/dd/yyyy - mm/dd/yyyy',
        extra: {
            startDate: 'checkIn',
            endDate: 'checkOut',
        }
    },
    {
        fieldName: 'propertyFilter',
        fieldLabel: 'Property Filter',
        fieldType: 'text',
        required: false,
        // layout: {
        //     xs: 12,
        //     md: 6,
        // }
    },
    {
        fieldName: 'checkIn',
        fieldLabel: 'Check In',
        fieldType: 'date',
        required: true,
        hidden: true,

    },
    {
        fieldName: 'checkOut',
        fieldLabel: 'Check Out',
        fieldType: 'date',
        required: true,
        hidden: true,
    },
    {
        fieldName: 'showCheckIns',
        fieldLabel: 'Show Check Ins',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },
    {
        fieldName: 'showCheckOuts',
        fieldLabel: 'Show Check Outs',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },
    {
        fieldName: 'showOccupied',
        fieldLabel: 'Show Occupied',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },
    {
        fieldName: 'showCleanings',
        fieldLabel: 'Show Cleanings',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },
    {
        fieldName: 'showOverdue',
        fieldLabel: 'Show Overdue',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },
    {
        fieldName: 'showToday',
        fieldLabel: 'Show Today',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },
    {
        fieldName: 'showEmpty',
        fieldLabel: 'Show Empty',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },
    {
        fieldName: 'allProperties',
        fieldLabel: 'All Properties',
        fieldType: 'checkbox',
        required: false,
        layout: {
            xs: 6,
            md: 3,
        }
    },

]